import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import { api } from "../lib/api";
import { Page, Spinner } from "../components/Brand";
import { AGE_RANGES, DIAGNOSES, EF_RANGES, PREGNANCY, CONTRACEPTION, PLANS, URGENCIES } from "../lib/fr";

function PickerGrid({ label, options, value, onChange, columns = 2, testid }) {
  return (
    <div>
      <div className="cs-label mb-3">{label}</div>
      <div className={`grid gap-2.5`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))` }} data-testid={testid}>
        {options.map((opt) => (
          <button
            type="button"
            key={opt.v}
            onClick={() => onChange(opt.v)}
            className={`cs-picker ${value === opt.v ? "active" : ""}`}
            data-testid={`${testid}-${opt.v}`}
          >
            <div className="title">{opt.l}</div>
            {opt.d && <div className="desc">{opt.d}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function NewReferral() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    age_range: "",
    diagnosis: "",
    ef_range: "",
    pregnancy_status: "",
    current_contraception: "",
    contraception_plan: "none_yet",
    estrogen_contraindicated: false,
    urgency: "routine",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const required = ["age_range","diagnosis","ef_range","pregnancy_status","current_contraception","urgency"];
  const isValid = required.every((k) => form[k]);

  async function submit(e) {
    e.preventDefault();
    if (!isValid) return toast.error("Veuillez compléter tous les champs obligatoires");
    setBusy(true);
    try {
      const { data } = await api.post("/referrals", form);
      toast.success(`Référence créée — ${data.referral_code}`);
      setTimeout(() => navigate("/im"), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  }

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <button onClick={() => navigate(-1)} className="text-[14px] mb-4 inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--cs-text-muted)" }} data-testid="back-link">
        <ChevronLeft size={16} /> Retour
      </button>
      <div className="mb-7">
        <div className="cs-label">Médecine Interne</div>
        <h1 className="font-semibold mt-1" style={{ fontSize: "30px", letterSpacing: "-0.6px" }}>Nouvelle référence</h1>
        <p className="text-[14px] mt-1" style={{ color: "var(--cs-text-muted)" }}>
          Aucune information patient identifiable n'est demandée.
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-8" data-testid="new-referral-form">
        <section className="cs-card p-5 md:p-6 flex flex-col gap-6">
          <PickerGrid label="Tranche d'âge" options={AGE_RANGES} value={form.age_range}
                       onChange={(v) => set("age_range", v)} columns={3} testid="picker-age" />
          <PickerGrid label="Diagnostic" options={DIAGNOSES} value={form.diagnosis}
                       onChange={(v) => set("diagnosis", v)} columns={2} testid="picker-diagnosis" />
          <PickerGrid label="FEVG" options={EF_RANGES} value={form.ef_range}
                       onChange={(v) => set("ef_range", v)} columns={2} testid="picker-ef" />
        </section>

        <section className="cs-card p-5 md:p-6 flex flex-col gap-6">
          <PickerGrid label="Statut grossesse" options={PREGNANCY} value={form.pregnancy_status}
                       onChange={(v) => set("pregnancy_status", v)} columns={3} testid="picker-pregnancy" />
          <PickerGrid label="Contraception actuelle" options={CONTRACEPTION} value={form.current_contraception}
                       onChange={(v) => set("current_contraception", v)} columns={3} testid="picker-current" />
          <PickerGrid label="Plan contraception envisagé" options={PLANS} value={form.contraception_plan}
                       onChange={(v) => set("contraception_plan", v)} columns={3} testid="picker-plan" />
        </section>

        <section className="cs-card p-5 md:p-6 flex flex-col gap-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="cs-label mb-1">Œstrogènes contre-indiqués</div>
              <div className="text-[13px]" style={{ color: "var(--cs-text-muted)" }}>
                Cocher si toute méthode contenant des œstrogènes est à éviter.
              </div>
            </div>
            <button
              type="button"
              onClick={() => set("estrogen_contraindicated", !form.estrogen_contraindicated)}
              className={`cs-toggle ${form.estrogen_contraindicated ? "on" : ""}`}
              data-testid="toggle-estrogen"
              aria-pressed={form.estrogen_contraindicated}
            />
          </div>

          <PickerGrid label="Urgence" options={URGENCIES} value={form.urgency}
                       onChange={(v) => set("urgency", v)} columns={3} testid="picker-urgency" />

          <div>
            <div className="cs-label mb-2">Notes cliniques (sans PII)</div>
            <textarea
              className="cs-input"
              rows={4}
              placeholder="Contexte clinique, traitements en cours, particularités… (interdire tout nom, date de naissance, MRN, etc.)"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              data-testid="notes-input"
            />
          </div>
        </section>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button type="button" onClick={() => navigate("/im")} className="cs-btn-ghost" data-testid="cancel-button">Annuler</button>
          <button type="submit" disabled={busy || !isValid} className="cs-btn-primary" data-testid="submit-referral">
            {busy ? <Spinner /> : "Créer la référence"}
          </button>
        </div>
      </form>
    </Page>
  );
}
